// -----------------------------------------------------------------------------
// src/pages/.../CreatorRow.jsx        – cooperates with dialog-side saving
// -----------------------------------------------------------------------------
import React, { useMemo, useState } from "react";
import {
  Avatar,
  Badge,
  Box,
  Button,
  Chip,
  IconButton,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Paper,
  Typography,
  Tooltip,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import ChatBubbleOutlineIcon from "@mui/icons-material/ChatBubbleOutline";
import PersonRemoveIcon from "@mui/icons-material/PersonRemove";
import { useQuery } from "react-query";
import client from "../../../../API";
import {
  getFollowersByVideos,
  getTotalFollowersInDict,
} from "../../../../Utils/constants";
import CreatorEditDialog from "./extras/CampaignCreatorEditDialog";
import CreatorChatDrawer from "./extras/CampaignCreatorChatDrawer";

/* helpers ------------------------------------------------------------------ */
const isVideoPaymentEligible = (v) =>
  !!v.liveLink && v.crossposts.every((c) => !!c.liveLink) && !v.payoutComplete;

// Status tooltip messages
const getStatusTooltip = (status, payoutComplete) => {
  const baseTooltips = {
    "Pitched": "Initial outreach sent - waiting for creator response",
    "Counter": "Creator has made a counter-offer - negotiation required",
    "Accepted": "Creator has accepted the campaign terms and pricing",
    "Approved": "Creator's content has been approved and campaign is complete",
    "Declined": "Creator has declined to participate in this campaign",
    "Dropped": "Creator has been removed from the campaign",
    "Pending": "Waiting for creator action or approval",
    "Draft": "Creator added but campaign not yet launched",
    "Launched": "Campaign is live and creator has been contacted",
    "Completed": "All deliverables completed and approved"
  };

  let tooltip = baseTooltips[status] || `Status: ${status}`;
  
  if (payoutComplete) {
    tooltip += " • Payment has been processed";
  }
  
  return tooltip;
};

// Calculate days since ideal due date (positive means overdue)
const getDaysSinceIdealDueDate = (idealDueDate) => {
  if (!idealDueDate) return 0;
  
  // Handle both string and Date objects
  const dueDate = new Date(idealDueDate);
  const now = new Date();
  
  // Set time to end of due date for fair comparison
  dueDate.setHours(23, 59, 59, 999);
  
  return Math.floor((now - dueDate) / (1000 * 60 * 60 * 24));
};

// Check if campaign is overdue based on ideal due date
const isCampaignOverdue = (idealDueDate) => {
  return getDaysSinceIdealDueDate(idealDueDate) > 0;
};

/* component ---------------------------------------------------------------- */
export default function CreatorRow({
  creator,
  campaignDetails,
  index,
  isLaunched,
  isTcc,
  handleFieldChange,        // (creatorId, field, value, markUnsaved?)
  handleDialogOpen,         // opens file picker for brief uploads
  handleResendOfferClick,
  confirmDropCreator,
  completeCreator,
  completeVideo,
  setSelectedCreator,
  setNegotiationDialogOpen,
  saveChanges,              // still passed from parent – not used here
}) {
  /* local UI state */
  const [editOpen, setEditOpen] = useState(false);
  const [chatOpen, setChatOpen] = useState(false);

  /* notifications / unread messages */
  const notifCount = creator.notifications?.length || 0;
  const { data: unreadData } = useQuery(
    ["unreadCount", campaignDetails.id, creator.id],
    () =>
      client.conversations.getUnreadCountForCampaignCreator(
        campaignDetails.id,
        creator.id
      ),
    { enabled: isLaunched, refetchInterval: 60_000, refetchOnWindowFocus: false }
  );
  const unreadCount = unreadData?.unread_count ?? 0;

  /* calculate days since ideal due date for drop creator feature */
  const daysSinceIdealDue = getDaysSinceIdealDueDate(
    campaignDetails.ideal_duedate || campaignDetails.idealDueDate
  );
  const isOverdue = isCampaignOverdue(
    campaignDetails.ideal_duedate || campaignDetails.idealDueDate
  );
  
  // Show drop button for pitched creators when campaign is overdue by 3+ days
  const shouldShowDropButton = 
    creator.status === "Pitched" && 
    isLaunched && 
    daysSinceIdealDue >= 3;

  /* videos & payment eligibility */
  const videoList = useMemo(
    () =>
      creator.videos
        .filter((v) => v.parentId == null)
        .map((v) => ({
          ...v,
          crossposts: creator.videos.filter((c) => c.parentId === v.id),
        })),
    [creator.videos]
  );
  const unpaid        = videoList.filter((v) => !v.payoutComplete);
  const eligibleVids  = unpaid.filter(isVideoPaymentEligible);
  const allEligible   = unpaid.length > 0 && unpaid.length === eligibleVids.length;

  const PaymentButtons = () => {
    if (creator.skipPayout || creator.payoutComplete) return null;

    if (allEligible) {
      return (
        <Tooltip title="Process payment for all eligible videos from this creator" arrow>
          <Button
            variant="contained"
            color="secondary"
            onClick={() => completeCreator(creator.id)}
            sx={{ minWidth: 160 }}
          >
            Complete Payment
          </Button>
        </Tooltip>
      );
    }
    return eligibleVids.map((v) => (
      <Tooltip key={v.id} title={`Process payment for video ${v.id} specifically`} arrow>
        <Button
          variant="contained"
          color="secondary"
          onClick={() => completeVideo(creator.id, v.id)}
          sx={{ minWidth: 140 }}
        >
          {`Pay Video ${v.id}`}
        </Button>
      </Tooltip>
    ));
  };

  /* render ------------------------------------------------------------------ */
  return (
    <>
      <Paper
        elevation={1}
        sx={{
          mb: 2,
          borderRadius: 2,
          border: `2px solid ${
            creator.status === "Accepted"
              ? "#4CAF50"
              : creator.status === "Counter"
              ? "#FFEB3B"
              : creator.status === "Approved"
              ? "#2196F3"
              : "#e0e0e0"
          }`,
        }}
      >
        <ListItem sx={{ flexWrap: "wrap", gap: 1 }}>
          {/* avatar + notif badge */}
          <ListItemAvatar>
            <Tooltip 
              title={notifCount > 0 ? `${notifCount} new notification${notifCount !== 1 ? 's' : ''}` : creator.name}
              arrow
            >
              <Badge
                overlap="circular"
                badgeContent={notifCount}
                color="error"
                anchorOrigin={{ vertical: "top", horizontal: "right" }}
              >
                <Avatar src={creator.pfphref} alt={creator.id} />
              </Badge>
            </Tooltip>
          </ListItemAvatar>

          {/* name & status */}
          <ListItemText
            primary={
              <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
                <Typography variant="subtitle1">{creator.name}</Typography>
                <Tooltip 
                  title={getStatusTooltip(creator.status, creator.payoutComplete)}
                  arrow
                  placement="top"
                >
                  <Chip
                    size="small"
                    label={
                      creator.status + (creator.payoutComplete ? " + Paid" : "")
                    }
                    sx={{
                      backgroundColor:
                        creator.status === "Accepted"
                          ? "#4CAF50"
                          : creator.status === "Counter"
                          ? "#FFEB3B"
                          : creator.status === "Approved"
                          ? "#2196F3"
                          : "#e0e0e0",
                      color:
                        creator.status === "Counter" ||
                        creator.status === "Pitched"
                          ? "rgba(0,0,0,0.87)"
                          : "white",
                      cursor: "help",
                      '&:hover': {
                        opacity: 0.8,
                      }
                    }}
                  />
                </Tooltip>
                {/* Show overdue status for pitched creators */}
                {creator.status === "Pitched" && isLaunched && (
                  <Chip
                    size="small"
                    label={
                      isOverdue 
                        ? `${daysSinceIdealDue} days overdue`
                        : daysSinceIdealDue < 0 
                          ? `${Math.abs(daysSinceIdealDue)} days left`
                          : "Due today"
                    }
                    variant="outlined"
                    sx={{
                      borderColor: isOverdue ? "#f44336" : daysSinceIdealDue === 0 ? "#ff9800" : "#4caf50",
                      color: isOverdue ? "#f44336" : daysSinceIdealDue === 0 ? "#ff9800" : "#4caf50",
                      fontWeight: isOverdue ? 600 : 400,
                    }}
                  />
                )}
              </Box>
            }
            secondary={`Followers: ${
              getTotalFollowersInDict(getFollowersByVideos(creator.videos))
                ?.toLocaleString() || "N/A"
            }`}
          />

          {/* right-side buttons */}
          <Box sx={{ display: "flex", gap: 1, ml: "auto", flexWrap: "wrap" }}>
            {/* negotiate */}
            {creator.status === "Counter" && (
              <Tooltip title="Open negotiation dialog to respond to creator's counter-offer" arrow>
                <Button
                  variant="contained"
                  color="warning"
                  onClick={() => {
                    setSelectedCreator(creator);
                    setNegotiationDialogOpen(true);
                  }}
                >
                  Negotiate
                </Button>
              </Tooltip>
            )}

            {/* resend offer & chat (only after launch) */}
            {isLaunched && (
              <>
                {creator.status === "Pitched" && (
                creator.awaiting_creator_after_brand_counter ? (
                  <Tooltip
                    title="You proposed a new offer mid-negotiation. Resend is hidden so you don't double-send; wait for the creator to respond (or use chat)."
                    arrow
                  >
                    <span>
                      <Button variant="outlined" color="secondary" disabled sx={{ minWidth: 140 }}>
                        Awaiting creator
                      </Button>
                    </span>
                  </Tooltip>
                ) : (
                  <Tooltip title="Resend the campaign offer to this creator" arrow>
                    <Button
                      variant="contained"
                      color="primary"
                      onClick={() => handleResendOfferClick(creator.name)}
                      sx={{
                        minWidth: 120,
                      }}
                    >
                      Resend Offer
                    </Button>
                  </Tooltip>
                )
              )}

                {/* Drop Creator Button for unresponsive creators past due date */}
                {shouldShowDropButton && (
                  <Tooltip 
                    title={`Campaign is ${daysSinceIdealDue} days overdue and creator hasn't responded. Click to remove them from the campaign.`}
                    arrow
                  >
                    <Button
                      variant="outlined"
                      color="error"
                      startIcon={<PersonRemoveIcon />}
                      onClick={() => confirmDropCreator(creator.id)}
                      sx={{
                        borderColor: "#f44336",
                        color: "#f44336",
                        '&:hover': {
                          backgroundColor: "rgba(244, 67, 54, 0.04)",
                          borderColor: "#d32f2f",
                        }
                      }}
                    >
                      Drop Creator
                    </Button>
                  </Tooltip>
                )}

                <PaymentButtons />

                <Tooltip 
                  title={unreadCount > 0 ? `${unreadCount} unread message${unreadCount !== 1 ? 's' : ''} - Click to open chat` : "Open chat with this creator"}
                  arrow
                >
                  <Badge
                    badgeContent={unreadCount}
                    color="error"
                    invisible={unreadCount === 0}
                  >
                    <IconButton
                      size="small"
                      color="primary"
                      onClick={() => setChatOpen(true)}
                    >
                      <ChatBubbleOutlineIcon />
                    </IconButton>
                  </Badge>
                </Tooltip>
              </>
            )}

            {/* edit */}
            <Tooltip title="Edit creator details, pricing, and campaign settings" arrow>
              <IconButton size="small" onClick={() => setEditOpen(true)}>
                <EditIcon />
              </IconButton>
            </Tooltip>
          </Box>
        </ListItem>
      </Paper>

      {/* -------- dialogs & drawers ----------------------------------------- */}
      <CreatorEditDialog
        open={editOpen}
        onClose={() => setEditOpen(false)}
        creator={creator}
        campaignId={campaignDetails.id}
        index={index}
        handleUploadClick={handleDialogOpen}
        /* map new dialog signature -> parent */
        onFieldChange={(field, value, markUnsaved) =>
          handleFieldChange(creator.id, field, value, markUnsaved)
        }
        onDropCreator={() => confirmDropCreator(creator.id)}
        creatorId={creator.name}
        isTcc={isTcc}
      />

      <CreatorChatDrawer
        open={chatOpen}
        onClose={() => setChatOpen(false)}
        campaignId={campaignDetails.id}
        creatorId={creator.id}
        creatorName={creator.name}
        creatorPfp={creator.pfphref}
      />
    </>
  );
}